/* eslint-disable import/no-unresolved */
import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import maplibregl from 'maplibre-gl';
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-csp-worker.js?url';
import markerIcon from '@img/icons/map-marker-alt-filled.svg?raw';

import MaplibreMapProvider from './maplibre-map.context';
import { addLayoutLayers } from './maplibre-map.utils';

import 'maplibre-gl/dist/maplibre-gl.css';
import './maplibre-map.css';

const MAP_STYLE_URL = `https://api.maptiler.com/maps/base-v4-light/style.json?key=${import.meta.env.VITE_MAPTILER_KEY}`;

function assignRef(ref, value) {
  if (!ref) {
    return;
  }

  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  ref.current = value;
}

const MaplibreMap = forwardRef(function MaplibreMap({
  initialCenter = [0, 0],
  initialZoom = 5,
  children,
  className = '',
  showNavigationControls = false,
}, ref) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const initialCenterRef = useRef(initialCenter);
  const initialZoomRef = useRef(initialZoom);
  const initialShowNavigationControlsRef = useRef(showNavigationControls);

  const [map, setMap] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleMapContainerRef = useCallback((node) => {
    mapContainerRef.current = node;
    assignRef(ref, node);
  }, [ref]);

  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current) {
      return undefined;
    }

    maplibregl.setWorkerUrl(maplibreWorkerUrl);

    const mapInstance = new maplibregl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE_URL,
      center: initialCenterRef.current,
      zoom: initialZoomRef.current,
    });

    if (initialShowNavigationControlsRef.current) {
      mapInstance.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    }

    mapInstanceRef.current = mapInstance;
    setMap(mapInstance);

    const handleLoad = () => {
      addLayoutLayers(mapInstance);
      setIsLoading(false);

      const coloredSvg = markerIcon.replace('currentColor', '#44B0E1');
      const svgString = coloredSvg.replace('<svg', '<svg width="28" height="36"');
      const svgDataUri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;

      const img = new Image();
      img.onload = () => {
        if (!mapInstance.hasImage('measurement-station-pin')) {
          mapInstance.addImage('measurement-station-pin', img);
        }
      };
      img.src = svgDataUri;
    };

    mapInstance.on('load', handleLoad);

    return () => {
      mapInstance.off('load', handleLoad);
      mapInstance.remove();
      mapInstanceRef.current = null;
      setMap(null);
      setIsLoading(true);
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      mapInstanceRef.current?.resize();
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <MaplibreMapProvider value={{ isLoading, map }}>
      <div className={`maplibre-map ${className}`.trim()} ref={handleMapContainerRef}>
        {children}
      </div>
    </MaplibreMapProvider>
  );
});

export default MaplibreMap;
